import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Backspace";

/**
 * Dependency-free canvas signature pad. Emits the drawn signature as a PNG data URL via onChange
 * ({ dataUrl, base64Data, contentType }), or null when cleared. Supports mouse and touch.
 */
export const SignaturePad = ({ onChange, height = 170, penColor = "#1C2025" }) => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Size the canvas to its rendered width at device-pixel resolution for crisp lines.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.offsetWidth || 300;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = penColor;
    ctxRef.current = ctx;
  }, [height, penColor]);

  const pointFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches && e.touches[0] ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startStroke = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const { x, y } = pointFromEvent(e);
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(x, y);
  };

  const moveStroke = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = pointFromEvent(e);
    ctxRef.current.lineTo(x, y);
    ctxRef.current.stroke();
    if (!hasInk) setHasInk(true);
  };

  const endStroke = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    emit();
  };

  const emit = () => {
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.({ dataUrl, base64Data: dataUrl.split(",")[1], contentType: "image/png" });
  };

  const clear = () => {
    const canvas = canvasRef.current;
    ctxRef.current.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          border: "2px dashed",
          borderColor: "grey.400",
          borderRadius: 2,
          backgroundColor: "#fff",
          overflow: "hidden",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height, display: "block", touchAction: "none", cursor: "crosshair" }}
          onMouseDown={startStroke}
          onMouseMove={moveStroke}
          onMouseUp={endStroke}
          onMouseLeave={endStroke}
          onTouchStart={startStroke}
          onTouchMove={moveStroke}
          onTouchEnd={endStroke}
        />
        {!hasInk && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              position: "absolute",
              top: "50%",
              left: 0,
              right: 0,
              textAlign: "center",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          >
            Draw your signature here
          </Typography>
        )}
      </Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
        <Button size="small" startIcon={<ClearIcon />} onClick={clear} disabled={!hasInk}>
          Clear
        </Button>
      </Stack>
    </Box>
  );
};

export default SignaturePad;
